/**
 * Standortfreigabe — geteilte Definition für UI und Server.
 *
 * Die Dauern sind eine feste, kurze Liste (Vorgabe Dan, 2026-09-08). Bewusst NICHT
 * frei eingebbar: Ein Auswahlfeld mit vier großen Knöpfen ist für alle drei
 * Generationen bedienbar, ein Zeit-Picker nicht (Anf. 53–56).
 *
 * Der Server akzeptiert ausschließlich diese Werte. Ein Client, der eine andere
 * Dauer schickt, bekommt `validation_failed` — Ablaufzeiten werden nicht vom Client
 * bestimmt, sondern hier abgeleitet.
 */
export const SHARE_DURATIONS = [
    { key: 'm10', minutes: 10, label: '10 Minuten' },
    { key: 'm30', minutes: 30, label: '30 Minuten' },
    { key: 'm60', minutes: 60, label: '1 Stunde' },
    /** Kein Ablauf. In der UI heißt das „Bis ich es stoppe" — siehe Hinweis unten. */
    { key: 'until_stopped', minutes: null, label: 'Bis ich es stoppe' },
];
export function isShareDuration(value) {
    return SHARE_DURATIONS.some((d) => d.key === value);
}
/** Ablaufzeit serverseitig ableiten. `null` = dauerhaft, bis der Nutzer stoppt. */
export function expiryFor(key, now = new Date()) {
    const d = SHARE_DURATIONS.find((x) => x.key === key);
    if (!d)
        throw new Error(`unknown share duration: ${key}`);
    return d.minutes === null ? null : new Date(now.getTime() + d.minutes * 60_000);
}
/**
 * Woher eine Freigabe stammt. Es gibt keinen Ursprung, der ohne Zutun des
 * Betroffenen entsteht — außer `sos`, und der ist zeitlich begrenzt und im
 * Audit-Log sichtbar.
 */
export const SHARE_ORIGINS = [
    'self', // der Betroffene hat selbst geteilt
    'request_approved', // jemand hat gefragt, der Betroffene hat zugestimmt
    'guardian_visible', // Sorgeberechtigte; dem Kind IMMER sichtbar angezeigt
    'sos', // automatisch bei Alarm, endet mit dem Alarm
    'arrival', // einmalig, "Bin sicher angekommen"
];
/** Genauigkeit, die der Teilende wählt. */
export const SHARE_PRECISIONS = [
    { key: 'exact', label: 'Genauer Ort' },
    { key: 'area', label: 'Ungefähre Gegend', gridMeters: 1000 },
    { key: 'city', label: 'Nur die Stadt' },
];
/**
 * Ab wann eine Position als veraltet gilt und in der UI deutlich markiert wird.
 *
 * Hintergrund: Ein Browser aktualisiert den Standort nur bei geöffneter App.
 * „Bis ich es stoppe" heißt deshalb nicht Live-Tracking, sondern „letzter bekannter
 * Ort". Eine App, die das verschweigt, ist im Notfall gefährlich, weil man sich
 * darauf verlässt (siehe docs/RISIKEN-UND-ALTERNATIVEN.md §7).
 */
export const STALE_AFTER_MINUTES = 15;
export function isStale(recordedAt, now = new Date()) {
    return now.getTime() - recordedAt.getTime() > STALE_AFTER_MINUTES * 60_000;
}
/**
 * Rastergröße je Genauigkeitsstufe, in Metern.
 *
 * `city` ist mit 10 km grob genug, dass innerhalb einer Stadt keine Aussage
 * mehr über den Stadtteil möglich ist — genau das ist der Zweck.
 */
const RASTER_METER = {
    exact: 0,
    area: 1000,
    city: 10_000,
};
/** Ein Breitengrad sind rund 111,32 km — überall auf der Erde. */
const METER_PRO_GRAD_LAT = 111_320;
/**
 * Reduziert eine Position auf die gewünschte Genauigkeit.
 *
 * Gerastert statt zufällig verrauscht: Rauschen ließe sich durch Mittelung
 * über viele Abfragen wieder herausrechnen. Ein Raster nicht — wer eine Stunde
 * lang jede Minute abfragt, bekommt eine Stunde lang dieselbe Zelle.
 *
 * Die Ost-West-Rasterung berücksichtigt den Breitengrad. Ohne das wäre eine
 * Zelle in Hamburg deutlich schmaler als eine in Rom, und „1 km" bedeutete je
 * nach Wohnort etwas anderes.
 */
export function reduziere(position, precision) {
    const raster = RASTER_METER[precision];
    if (raster === 0)
        return position;
    const gradLat = raster / METER_PRO_GRAD_LAT;
    const lat = Math.round(position.lat / gradLat) * gradLat;
    // Nahe den Polen geht der Kosinus gegen null; ohne Untergrenze würde die
    // Zellenbreite gegen unendlich laufen und die Länge unbrauchbar.
    const kosinus = Math.max(Math.cos((lat * Math.PI) / 180), 0.01);
    const gradLon = raster / (METER_PRO_GRAD_LAT * kosinus);
    const lon = Math.round(position.lon / gradLon) * gradLon;
    return {
        lat: Number(lat.toFixed(6)),
        lon: Number(lon.toFixed(6)),
        // Die gemeldete Genauigkeit muss die Rasterung widerspiegeln. Sonst zeigt
        // die Karte einen 20-Meter-Kreis um einen auf 1 km gerundeten Punkt — eine
        // Präzision, die es nicht gibt.
        accuracy: Math.max(position.accuracy, raster),
    };
}
/**
 * Die feinste Genauigkeit unter mehreren Freigaben.
 *
 * Der Client fragt danach, BEVOR er sendet: Sind alle aktiven Freigaben auf
 * „Nur die Stadt" gestellt, verlässt der genaue Ort das Gerät gar nicht erst.
 * Nur nachträglich zu reduzieren wäre ein schwächeres Versprechen als das,
 * was die Oberfläche verspricht.
 */
export function feinsteGenauigkeit(precisions) {
    if (precisions.length === 0)
        return null;
    if (precisions.includes('exact'))
        return 'exact';
    if (precisions.includes('area'))
        return 'area';
    return 'city';
}
//# sourceMappingURL=location.js.map